import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const WingForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [sites, setSites] = useState([]);
    const [formData, setFormData] = useState({
        siteId: null,
        wingName: '',
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editId = location.state?.id;

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Sites for dropdown
                const siteRes = await apiServices({
                    method: 'GET',
                    url: '/Residential/ResSiteDetailsMaster/GetAllSiteDetails'
                });

                if (siteRes.success) {
                    let siteData = siteRes.data?.Data || [];

                    const formattedSites = siteData
                        .map(s => ({
                            label: s.SiteName,
                            value: s.Id
                        }));
                    setSites(formattedSites);
                }

                // If edit mode, fetch wing details
                if (isEdit) {
                    setIsFetching(true);
                    const wingRes = await apiServices({
                        method: 'GET',
                        url: `/Residential/ResWingMaster/GetWingById/${editId}`
                    });
                    if (wingRes.success) {
                        const data = wingRes.data?.WingMasterData?.[0] || {};
                        setFormData({
                            siteId: data.SiteId || null,
                            wingName: data.WingName || '',
                            isActive: data.IsActive || 'Active'
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            } finally {
                setIsFetching(false);
            }
        };

        fetchMasterData();
    }, [isEdit, editId]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => ({ ...prev, [key]: option ? option.value : null }));
    };

    const validateForm = () => {
        if (!formData.siteId) {
            toast.warning("Site is required");
            return false;
        }
        if (!formData.wingName || !formData.wingName.trim()) {
            toast.warning("Wing Name is required");
            return false;
        }
        if (!formData.isActive) {
            toast.warning("Status is required");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            siteId: formData.siteId || 1,
            wingName: formData.wingName,
            createdBy: 'sys',
            isActiveId: formData.isActive === 'Active' ? 1 : 0
        };

        try {
            const url = isEdit ? `/Residential/ResWingMaster/putWing/${editId}` : `/Residential/ResWingMaster/postWing`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success && (response.data?.IsSuccess || response.data?.isSuccess || response.data?.success || response.IsSuccess)) {
                toast.success(response.data?.Message || response.data?.message || `Wing ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentres/site-management/wing');
            } else {
                toast.error(response.data?.Message || response.data?.message || response.message || `Failed to ${isEdit ? 'update' : 'create'} wing.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    const selectedSite = sites.find(s => s.value === formData.siteId) || null;

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Wing`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} wing.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <CustomSelect
                    label={<>Site Name <span className="text-red-500">*</span></>}
                    options={sites}
                    value={selectedSite}
                    onChange={(opt) => handleSelectChange('siteId', opt)}
                />

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="wingName">
                        Wing Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="wingName"
                        value={formData.wingName}
                        onChange={handleInputChange}
                        placeholder="e.g., North Wing"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentres/site-management/wing")}
                    className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default WingForm;
